/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable no-param-reassign */
/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Card, CardContent } from '@mui/material';
import TextLink from 'src/components/common/TextLink';
import loaderImage from '../assets/images/loader.gif';
import END_POINT from '../config/endpoints.constants';
import AnonymousLayout from '../components/AnonymousLayout';
import withoutAuth from '../hoc/withoutAuth';
import SEO from '../components/common/SEO';
import Button from '../components/common/Button';
import { SITE_DESCRIPTION } from '../config/seo.constants';
import regionConfig from '../config';
import { useToast } from '../context/ToastContext';
import Messages from '../config/messages.constants';

const StyledVerifyCustomerEmail = styled(AnonymousLayout)`
    .verified-text-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 32px;
    }
    .verification-container {
        display: flex;
        justify-content: center;
    }
    .verified-text {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }
    .underlineText {
        text-decoration: underline;
        padding: 10px;
        display: inline-block;
        cursor: pointer;
        color: #1884eb;
        a {
            font-weight: 700;
        }
    }
    .heading {
        font-size: 32px;
        font-weight: 700;
        line-height: 40px;
    }
    .instructions {
        font-size: 16px;
        font-weight: 600;
        color: var(--black-60, rgba(17, 17, 17, 0.6));
    }
`;

const getUrlParameter = (search: string, name: string) => {
    // eslint-disable-next-line no-useless-escape
    const safeName = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
    const regex = new RegExp(`[\\?&]${safeName}=([^&#]*)`);
    const results = regex.exec(search);
    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
};

const VerifyCustomerEmail: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const toast = useToast();

    const token = getUrlParameter(location.search, 'token');

    const [inProgress, setInProgress] = useState(true);
    const [isVerified, setIsVerified] = useState(false);
    const [linkExpired, setLinkExpired] = useState(false);
    const [linkAlreadyUsed, setLinkAlreadyUsed] = useState(false);
    const [msg, setMsg] = useState<string | React.ReactNode>('');

    useEffect(() => {
        axios
            .post(
                END_POINT.CUSTOMER_VERIFY,
                {
                    token,
                    applicationName: 'customer-portal',
                },
                {
                    headers: {
                        'App-Name': 'userapp',
                        'App-Source': 'userapp',
                    },
                },
            )
            .then((res) => {
                if (res.data.code === 0) {
                    setInProgress(false);
                    setIsVerified(true);
                } else if (res.data.code === -3) {
                    setInProgress(false);
                    setIsVerified(true);
                    setLinkExpired(true);
                } else if (res.data.code === -1) {
                    setInProgress(false);
                    setIsVerified(false);
                    setMsg(res.data.message);
                }
            })
            .catch((err) => {
                setInProgress(false);
                setIsVerified(false);
                if (err.response.status === 400 && err.response.data.code === 1) {
                    setLinkAlreadyUsed(true);
                    toast.error(Messages.LINK_ALREADY_USED);
                    setTimeout(() => navigate('/login'), 3000);
                } else {
                    setMsg(err.response.data.message);
                }
            });
    }, []);

    const handleLinkClick = (click: string) => {
        if (click === 'android') {
            window.open(regionConfig.androidLink, '_blank');
        } else if (click === 'ios') {
            window.open(regionConfig.iosLink, '_blank');
        } else if (click === 'web') {
            window.open(regionConfig.webLink, '_blank');
        }
    };

    return (
        <StyledVerifyCustomerEmail isLoginScreen={true}>
            <SEO
                title="Verify Customer Email"
                description={`${regionConfig.appName} Verify Profile - ${SITE_DESCRIPTION}`}
            />
            <div className="w-full h-auto mt-32 flex flex-col justify-center items-center card-container">
                {!linkAlreadyUsed && (
                    <Card className="card" style={{ maxWidth: '480px' }}>
                        <CardContent>
                            {inProgress ? (
                                <div>
                                    <div className="heading">
                                        Your Email verification is in Progress
                                    </div>

                                    <div style={{ textAlign: 'center' }}>
                                        <img
                                            alt=""
                                            src={loaderImage}
                                            style={{ display: 'inline', height: '50px' }}
                                        />
                                    </div>
                                </div>
                            ) : isVerified ? (
                                <div className="verified-text-container">
                                    <div className="verified-text">
                                        <div className="heading">
                                            Your email has been confirmed!
                                        </div>

                                        <div className="instructions">
                                            Please set your password.
                                        </div>
                                    </div>
                                    <Button
                                        id="resetPassword"
                                        size="large"
                                        fullWidth
                                        className="primary-bg-color normal-case source-sans-pro-font"
                                        type="button"
                                        onClick={() =>
                                            navigate(
                                                linkExpired
                                                    ? '/forgotpassword'
                                                    : `/resetpassword?token=${token}`,
                                            )
                                        }
                                    >
                                        Set password
                                    </Button>
                                    <div className="instructions w-full">
                                        Need the app? Get it here:
                                        <span className="underlineText">
                                            <TextLink
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleLinkClick('android');
                                                }}
                                            >
                                                Android
                                            </TextLink>
                                        </span>
                                        |
                                        <span className="underlineText">
                                            <TextLink
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleLinkClick('ios');
                                                }}
                                            >
                                                iOS
                                            </TextLink>
                                        </span>
                                        |
                                        <span className="underlineText">
                                            <TextLink
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleLinkClick('web');
                                                }}
                                            >
                                                Web
                                            </TextLink>
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <div className="instructions">{msg}</div>
                            )}
                        </CardContent>
                    </Card>
                )}
                {linkAlreadyUsed && (
                    <Button
                        id="loginNow"
                        size="large"
                        type="button"
                        className="primary-bg-color normal-case source-sans-pro-font"
                        style={{ marginTop: '24px' }}
                        onClick={() => navigate('/login')}
                    >
                        Log in now
                    </Button>
                )}
            </div>
        </StyledVerifyCustomerEmail>
    );
};

export default withoutAuth(VerifyCustomerEmail);
